import { useState, useEffect, useRef, RefObject } from 'react';
import { AvanceEntity, TimelineEntity } from '@/types/model';
import Image from 'next/image';
import { ImageLoader } from '@/lib/imageLoader';
import { calculateTimelineHeight } from '@/lib/helpers';
import s from './Timeline.module.css';
import { Button } from '@/components';
import { motion } from 'framer-motion';
import useIsMobile from '@/hooks/use-is-mobile';
import dynamic from 'next/dynamic';

const GalleryModal = dynamic(() => import('@/components/GalleryModal'), { ssr: false });

const container = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.4
    }
  }
};

const item = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

type CardProps = {
  avance: AvanceEntity;
  index: number;
  esUltimo: boolean;
  isMobile: boolean | null;
  showImages: string;
  showVideos: string;
  setShowImages: (id: string) => void;
  setShowVideos: (id: string) => void;
  lastMediaRef: RefObject<HTMLDivElement | null>;
};

const TimelineCard = ({
  avance: a,
  index,
  esUltimo,
  isMobile,
  showImages,
  showVideos,
  setShowImages,
  setShowVideos,
  lastMediaRef
}: CardProps) => {
  const side = Boolean(index % 2) && !isMobile;
  const isModalOpen = Boolean(showImages) || showVideos === a.id;

  const handleClose = () => {
    setShowImages('');
    setShowVideos('');
  };

  const handleChangeToVideo = (id: string) => {
    if (isModalOpen && showImages !== '') {
      setShowImages('');
      setShowVideos(id);
    }
    if (!isModalOpen) {
      setShowVideos(id);
    }
    if (isModalOpen && showVideos !== '') {
      setShowVideos(id);
    }
  };

  const handleChangeToImage = (id: string) => {
    if (isModalOpen && showVideos !== '') {
      setShowImages(id);
      setShowVideos('');
    }
    if (!isModalOpen) {
      setShowImages(id);
    }
    if (isModalOpen && showImages !== '') {
      setShowImages(id);
    }
  };

  return (
    <motion.div variants={item}>
      <motion.article className={`${s.card} ${side ? s.right : s.left}`}>
        <h2 className={`${s.card__title} ${side ? s.right__title : s.left__title}`}>
          {a.title}
          <span className={side ? s.right__preTitle : s.left__preTitle} />
        </h2>

        {a.poster && (
          <div
            className={s.card__media}
            ref={(el) => {
              if (esUltimo && el) {
                lastMediaRef.current = el;
              }
            }}>
            <Image
              src={ImageLoader({ src: a.poster?.url })}
              alt="image"
              width={1000}
              height={500}
              quality={65}
              sizes={isMobile ? '90vw' : '1000px'}
            />
          </div>
        )}

        <div className={s.card__actions}>
          {!!a.videos.length && (
            <Button
              label="VIDEOS"
              onClick={() => handleChangeToVideo(a.id)}
              svg="/icons/boton-video.svg"
            />
          )}
          {!!a.images?.length && (
            <Button
              label="PHOTOS"
              onClick={() => handleChangeToImage(a.id)}
              svg="/icons/boton-fotos.svg"
            />
          )}
        </div>
      </motion.article>

      {isModalOpen && (
        <GalleryModal
          id={a.id}
          onClose={handleClose}
          showImages={showImages}
          showVideos={showVideos}
          images={a.images}
          videos={a.videos}
        />
      )}
    </motion.div>
  );
};

const Timeline = ({ avance }: TimelineEntity) => {
  const [showImages, setShowImages] = useState('');
  const [showVideos, setShowVideos] = useState('');
  const isMobile = useIsMobile();
  const containerRef = useRef<HTMLElement | null>(null);
  const lastMediaRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const calculateHeight = () => {
      calculateTimelineHeight(containerRef.current, lastMediaRef.current);
    };

    calculateHeight();
    window.addEventListener('resize', calculateHeight);

    const timeout = setTimeout(calculateHeight, 500);

    return () => {
      window.removeEventListener('resize', calculateHeight);
      clearTimeout(timeout);
    };
  }, [avance, isMobile]);
  return (
    <motion.section
      ref={containerRef}
      className={s.timeline}
      variants={container}
      initial="hidden"
      animate="visible">
      {avance.map((a, i) => (
        <TimelineCard
          key={i}
          avance={a}
          index={i}
          esUltimo={i === avance.length - 1}
          isMobile={isMobile}
          showImages={showImages}
          showVideos={showVideos}
          setShowImages={setShowImages}
          setShowVideos={setShowVideos}
          lastMediaRef={lastMediaRef}
        />
      ))}
    </motion.section>
  );
};

export default Timeline;
